import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Clock3, Database, Gift, Users } from "lucide-react";
import { AdminLayout } from "../../components/AdminLayout";
import { AdminStatCard } from "../../components/admin/AdminStatCard";
import { adminHeaders, api } from "../../lib/api";
import { isLocalDevFrontend } from "../../lib/env";

interface MemberRow {
  id: number;
  status: string;
}

interface RewardRow {
  id: number;
  reward_status: string;
  created_at: string;
}

interface DepositRow {
  id: number;
  created_at: string;
  is_test: number;
}

const quickLinks = [
  { href: "/admin/rewards", title: "派發中心", text: "查看等待派發、已完成或需人工處理的派發單。" },
  { href: "/admin/reviews", title: "審核中心", text: "處理 LINE 群打卡、贈禮小幫手與人工審核申請。" },
  { href: "/admin/members", title: "會員查詢", text: "查會員狀態、新人七關、邀友與派發紀錄。" },
  { href: "/admin/invite", title: "邀友人工快審", text: "建立與追蹤邀友入群、首存、二存、三存獎勵。" },
  { href: "/admin/crawler", title: "贈禮小幫手", text: "查看原始贈禮紀錄並建立人工審核。" },
  { href: "/admin/deposits", title: "存款紀錄", text: "查詢會員存款資料與測試資料標記。" }
];

const systemNotes = [
  "派發單必須由派發中心確認後才算完成。",
  "贈禮小幫手的原始金額只作參考，實際派發金額需人工確認。",
  "正式部署前仍需確認 production D1、Cloudflare secrets 與 migration runbook。",
  "未正式開放的活動請不要從後台當作正式流程操作。"
];

export function AdminHomePage() {
  const isLocalDev = isLocalDevFrontend();
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [rewards, setRewards] = useState<RewardRow[]>([]);
  const [deposits, setDeposits] = useState<DepositRow[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("admin_token") ?? localStorage.getItem("adminToken");
    if (!token) {
      if (isLocalDev) {
        localStorage.setItem("admin_token", "jy-admin-test-token");
        localStorage.setItem("adminToken", "jy-admin-test-token");
      } else {
        setError("請先設定後台管理權杖，才能讀取營運資料。");
        return;
      }
    }

    Promise.all([
      api<MemberRow[]>("/api/admin/members", { headers: adminHeaders() }),
      api<RewardRow[]>("/api/admin/rewards", { headers: adminHeaders() }),
      api<DepositRow[]>("/api/admin/deposits", { headers: adminHeaders() })
    ])
      .then(([memberRows, rewardRows, depositRows]) => {
        setMembers(memberRows);
        setRewards(rewardRows);
        setDeposits(depositRows);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "後台總覽資料讀取失敗"));
  }, [isLocalDev]);

  const stats = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return {
      memberTotal: members.length,
      activeNewbies: members.filter((row) => ["channel_confirmed", "newbie_active"].includes(row.status)).length,
      pendingRewards: rewards.filter((row) => row.reward_status === "ready").length,
      completedRewards: rewards.filter((row) => row.reward_status === "completed").length,
      attentionRewards: rewards.filter((row) => ["failed", "canceled", "cancelled", "rejected"].includes(row.reward_status)).length,
      testDepositsToday: deposits.filter((row) => row.is_test && String(row.created_at ?? "").startsWith(today)).length
    };
  }, [members, rewards, deposits]);

  return (
    <AdminLayout title="後台總覽" subtitle="快速查看今日待處理項目，並前往常用營運頁面。">
      {error ? <p className="error-box">{error}</p> : null}

      <section className="admin-dashboard-grid">
        <AdminStatCard label="會員總數" value={stats.memberTotal} helper="目前系統內會員數" icon={Users} tone="purple" />
        <AdminStatCard label="新人進行中" value={stats.activeNewbies} helper="渠道已確認或新人七關進行中" icon={Clock3} />
        <AdminStatCard label="待派發" value={stats.pendingRewards} helper="等待客服確認派發" icon={Gift} tone="gold" />
        <AdminStatCard label="已完成派發" value={stats.completedRewards} helper="已確認派發完成" icon={CheckCircle2} tone="green" />
        <AdminStatCard label="需人工處理" value={stats.attentionRewards} helper="取消、失敗或退回紀錄" icon={AlertTriangle} tone="red" />
        {isLocalDev ? <AdminStatCard label="今日測試存款" value={stats.testDepositsToday} helper="本機測試資料" icon={Database} /> : null}
      </section>

      <section className="admin-card">
        <div className="admin-card-head">
          <span>常用入口</span>
          <h2>下一步要去哪裡</h2>
        </div>
        <div className="admin-quick-grid">
          {quickLinks.map((item) => (
            <a href={item.href} key={item.href}>
              <strong>{item.title}</strong>
              <span>{item.text}</span>
            </a>
          ))}
        </div>
      </section>

      <section className="admin-card">
        <div className="admin-card-head">
          <span>系統提醒</span>
          <h2>操作前請確認</h2>
        </div>
        <div className="admin-note-list">
          {systemNotes.map((note) => <p key={note}>{note}</p>)}
        </div>
      </section>

      <section className="admin-card">
        <div className="admin-card-head">
          <span>待處理分類</span>
          <h2>客服常用工作流</h2>
        </div>
        <div className="admin-status-grid">
          <div><span>派發</span><strong>先到派發中心確認待派發項目</strong></div>
          <div><span>審核</span><strong>到審核中心處理待確認申請</strong></div>
          <div><span>邀友</span><strong>到邀友人工快審建立邀友獎勵</strong></div>
          <div><span>贈禮</span><strong>到贈禮小幫手確認原始紀錄</strong></div>
        </div>
      </section>
    </AdminLayout>
  );
}
